import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import { DeviceType } from '@/src/types/deviceType'
import { DateFields, DocumentType, NonDocumentField, Passenger } from '@/src/types/itinerary'
import * as TravelerDocumentUtils from '@/src/utils/itinerary/travelerDocumentToInputsSegregater'
import { ValidationState, commonValidationRulesFn } from '@/src/utils/itinerary/travelerInputValidationRules'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import useGetItineraryId from '../../useGetItineraryId'
import useValidation from '../useValidation'

export const useValidationManager = ({
    deviceType,
    mandatoryFields,
    passengers,
    requiredDocuments,
    isDateOfBirthRequired,
    isNationalityRequiredToRenderInUserPersonalDetailsSection,
    arrivalDate,
    departureDate,
    isBirthCountryRequired
}: {
    requiredDocuments: DocumentType[]
    isDateOfBirthRequired: boolean
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    deviceType: DeviceType
    mandatoryFields: (DocumentType | NonDocumentField)[]
    passengers: Passenger[]
    arrivalDate: DateFields | null
    departureDate: DateFields | null
    isBirthCountryRequired: boolean
}) => {
    const itineraryId = useGetItineraryId()
    const validationTranslate = useTranslations('validation')

    // Convert departure/arrival dates to dayjs objects for validation reference
    const referenceDate = useMemo(() => {
        // Use arrival date if available, otherwise use departure date, otherwise use current date
        if (arrivalDate && arrivalDate.day && arrivalDate.month && arrivalDate.year) {
            return dayjs(`${arrivalDate.month} ${arrivalDate.day}, ${arrivalDate.year}`, 'MMMM D, YYYY', 'en')
        } else if (departureDate && departureDate.day && departureDate.month && departureDate.year) {
            return dayjs(`${departureDate.month} ${departureDate.day}, ${departureDate.year}`, 'MMMM D, YYYY', 'en')
        }
        return dayjs() // fallback to current date
    }, [arrivalDate, departureDate])

    const commonValidationRules = commonValidationRulesFn(validationTranslate, referenceDate)

    const { maxFirstNameLength, minLastNameLength } = useGetItineraryDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data }) => ({
                maxFirstNameLength: data?.meta?.maxPaxNameLength || 0,
                minLastNameLength: data?.meta?.minLastNameLength || 0
            })
        }
    )

    // Initialize validation state for travelers
    const initialTravelerValidationState = useMemo((): ValidationState => {
        const validationState: ValidationState = {}

        passengers.forEach((_, travelerIndex) => {
            // Personal details validation
            validationState[`traveler_${travelerIndex}_firstName`] = {
                isError: false,
                errorMessage: '',
                rules: [
                    commonValidationRules.required,
                    commonValidationRules.maxLength(maxFirstNameLength),
                    commonValidationRules.onlyLetters('pleaseProvideValidFirstNameAndMiddleName')
                ],
                touched: false
            }

            validationState[`traveler_${travelerIndex}_lastName`] = {
                isError: false,
                errorMessage: '',
                rules: [
                    commonValidationRules.required,
                    commonValidationRules.minLength(minLastNameLength),
                    commonValidationRules.onlyLetters('pleaseProvideValidLastName')
                ],
                touched: false
            }

            validationState[`traveler_${travelerIndex}_title`] = {
                isError: false,
                errorMessage: '',
                rules: [commonValidationRules.required],
                touched: false
            }

            if (isNationalityRequiredToRenderInUserPersonalDetailsSection) {
                validationState[`traveler_${travelerIndex}_nationality`] = {
                    isError: false,
                    errorMessage: '',
                    rules: [commonValidationRules.required],
                    touched: false
                }
            }

            if (isBirthCountryRequired) {
                validationState[`traveler_${travelerIndex}_birthCountry`] = {
                    isError: false,
                    errorMessage: '',
                    rules: [commonValidationRules.required],
                    touched: false
                }
            }

            if (isDateOfBirthRequired) {
                // Get the passenger type to determine which age validation rule to apply
                const passenger = passengers[travelerIndex]
                let ageValidationRule = commonValidationRules.validDate

                // Add age-specific validation based on passenger type
                if (passenger?.type === 'ADT') {
                    ageValidationRule = commonValidationRules.adultAge
                } else if (passenger?.type === 'CHD') {
                    ageValidationRule = commonValidationRules.childAge
                } else if (passenger?.type === 'INF') {
                    ageValidationRule = commonValidationRules.infantAge
                }

                validationState[`traveler_${travelerIndex}_dateOfBirth`] = {
                    isError: false,
                    errorMessage: '',
                    rules: [commonValidationRules.validDate, ageValidationRule],
                    touched: false
                }
            }

            // Document details validation
            requiredDocuments.forEach(docType => {
                const documentInputs = TravelerDocumentUtils.travelerDocumentToInputsSegregate(docType)

                // Add docNumber validation if it's required for this document type
                if (documentInputs.docNumber) {
                    validationState[`traveler_${travelerIndex}_${docType}_docNumber`] = {
                        isError: false,
                        errorMessage: '',
                        rules: [
                            commonValidationRules.required,
                            commonValidationRules.maxLength(15),
                            commonValidationRules.minLength(3),
                            commonValidationRules.onlyNumbersAndLetters('pleaseEnterValidDocumentNumber', false)
                        ],
                        touched: false
                    }
                }

                // Add nationality validation if it's required for this document type
                if (documentInputs.nationality) {
                    validationState[`traveler_${travelerIndex}_${docType}_nationality`] = {
                        isError: false,
                        errorMessage: '',
                        rules: [commonValidationRules.required],
                        touched: false
                    }
                }

                // Add countryIssued validation if it's required for this document type
                if (documentInputs.countryIssued) {
                    validationState[`traveler_${travelerIndex}_${docType}_countryIssued`] = {
                        isError: false,
                        errorMessage: '',
                        rules: [commonValidationRules.required],
                        touched: false
                    }
                }

                // Add expiryDate validation if it's required for this document type
                if (documentInputs.expiryDate) {
                    validationState[`traveler_${travelerIndex}_${docType}_expiryDate`] = {
                        isError: false,
                        errorMessage: '',
                        rules: [
                            commonValidationRules.required,
                            commonValidationRules.validDate,
                            commonValidationRules.futureDate
                        ],
                        touched: false
                    }
                }

                // Add issueDate validation if it's required for this document type
                if (documentInputs.issueDate) {
                    validationState[`traveler_${travelerIndex}_${docType}_issueDate`] = {
                        isError: false,
                        errorMessage: '',
                        rules: [
                            commonValidationRules.required,
                            commonValidationRules.validDate,
                            commonValidationRules.pastDate
                        ],
                        touched: false
                    }
                }
            })
        })

        return validationState
    }, [
        passengers,
        mandatoryFields,
        maxFirstNameLength,
        minLastNameLength,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired,
        requiredDocuments,
        commonValidationRules
    ])

    const travelerValidation = useValidation({ initialValidationState: initialTravelerValidationState })

    // Initialize validation state for contact details
    const initialContactValidationState = useMemo(
        (): ValidationState => ({
            email: {
                isError: false,
                errorMessage: '',
                rules: [commonValidationRules.required, commonValidationRules.email],
                touched: false
            },
            mobile: {
                isError: false,
                errorMessage: '',
                rules: [commonValidationRules.phone],
                touched: false
            },
            mobileCountryCode: {
                isError: false,
                errorMessage: '',
                rules: [commonValidationRules.required],
                touched: false
            }
        }),
        [commonValidationRules]
    )

    const contactValidation = useValidation({ initialValidationState: initialContactValidationState })

    // Initialize validation state for tax details
    const initialTaxValidationState = useMemo(
        (): ValidationState => ({
            companyName: {
                isError: false,
                errorMessage: '',
                rules: [
                    commonValidationRules.required,
                    commonValidationRules.onlyNumbersAndLetters('pleaseProvideValidCompanyName', true)
                ],
                touched: false
            },
            taxNo: {
                isError: false,
                errorMessage: '',
                rules: [
                    commonValidationRules.required,
                    commonValidationRules.minLength(10),
                    commonValidationRules.onlyNumbersAndLetters('pleaseProvideValidTaxDetails', false)
                ],
                touched: false
            }
        }),
        [commonValidationRules]
    )

    const taxValidation = useValidation({ initialValidationState: initialTaxValidationState })

    return {
        travelerValidation,
        contactValidation,
        taxValidation
    }
}
